import React from 'react';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { FilterSkeleton } from './index';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import './FilterSidebar.css';

const FilterCheckboxGroup = ({ title, filters, setFilters, scrollable = false }) => {
    const handleSelectAll = () => {
        const newFilters = {};
        Object.keys(filters).forEach(k => newFilters[k] = true);
        setFilters(newFilters);
    };

    const handleClearAll = () => {
        const newFilters = {};
        Object.keys(filters).forEach(k => newFilters[k] = false);
        setFilters(newFilters);
    };

    return (
        <div className="filter-section-container">
            <div className="filter-section-header">
                <h3 className="filter-section-title">{title}</h3>
                <div className="filter-actions-group">
                    <button
                        onClick={handleSelectAll}
                        className="filter-action-link select-all"
                    >
                        Select All
                    </button>
                    <span className="filter-action-separator">|</span>
                    <button
                        onClick={handleClearAll}
                        className="filter-action-link clear-all"
                    >
                        Clear All
                    </button>
                </div>
            </div>

            <div className={`filter-checkbox-group ${scrollable ? 'max-h-48 overflow-y-auto pr-2 custom-scrollbar' : ''}`}>
                {Object.entries(filters).map(([key, value]) => (
                    <label key={key} className="filter-checkbox-label">
                        <input
                            type="checkbox"
                            className="hidden"
                            checked={value}
                            onChange={() => setFilters({ ...filters, [key]: !value })}
                        />
                        <div className={`filter-checkbox-box ${value ? 'checked' : ''}`}>
                            {value && <Check size={14} className="text-white" strokeWidth={3} />}
                        </div>
                        <span className="filter-checkbox-text">{key}</span>
                    </label>
                ))}
            </div>
            <div className="filter-section-divider"></div>
        </div>
    );
};

const RangeSlider = ({ min, max, value, onChange, formatLabel = (v) => v }) => {
    // rc-slider expects an array for range: [min, max]
    const rangeValue = [value.min, value.max];

    const handleSliderChange = (newValues) => {
        onChange({ min: newValues[0], max: newValues[1] });
    };

    return (
        <div className="range-slider-container">
            <div className="sliders-control">
                <Slider
                    range
                    min={min}
                    max={max}
                    value={rangeValue}
                    onChange={handleSliderChange}
                    allowCross={false}
                    className="rc-custom-slider"
                />
            </div>
            <div className="slider-values">
                <span>{formatLabel(value.min)}</span>
                <span>{formatLabel(value.max)}</span>
            </div>
        </div>
    );
};

const FilterSidebar = ({
    stopsFilter,
    setStopsFilter,
    trainsFilter,
    setTrainsFilter,
    sourcesFilter,
    setSourcesFilter,
    destinationsFilter,
    setDestinationsFilter,
    viaFilter = {},
    setViaFilter = () => { },
    departureTimeFilter, // Expecting { min, max }
    setDepartureTimeFilter,
    durationFilter,      // Expecting { min, max }
    setDurationFilter,
    fareFilter,          // Expecting { min, max }
    setFareFilter,
    maxDeparture = 24,
    maxDuration = 0,
    maxFare = 0,
    isLoading = false,
    hideCancelled = false,
    setHideCancelled = () => { },
    showHideCancelled = false,
    showFullyAvailable = true,
    setShowFullyAvailable = () => { },
    avoidStationChange = false,

    setAvoidStationChange = () => { },
    showAvoidStationChange = false,
    // Accordion props for mobile
    isMobile = false,
    isOpen = false,
    onToggle = () => { }
}) => {
    // Only show skeleton if explicitly loading AND we have no filter data yet
    const hasFilters = Object.keys(stopsFilter).length > 0 ||
        Object.keys(trainsFilter).length > 0 ||
        Object.keys(sourcesFilter).length > 0;

    if (isLoading && !hasFilters) {
        if (isMobile) return null; // Don't show giant skeletons on mobile during load
        return <FilterSkeleton />;
    }

    // Helper to format minutes of the day into HH:mm
    const formatTime = (totalMinutes) => {
        const hour = Math.floor(totalMinutes / 60);
        const mins = totalMinutes % 60;
        return `${String(hour).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
    };

    return (
        <aside className={`filter-sidebar-main ${isMobile ? (isOpen ? 'open' : 'closed') : ''}`}>
            <div className="filter-sidebar-inner">
                <div
                    className={`filter-header-container ${isMobile ? 'cursor-pointer mobile-toggle' : ''}`}
                    onClick={isMobile ? onToggle : undefined}
                >
                    <div className="filter-header-flex-row">
                        <div className="filter-title-stack">
                            <h2 className="filter-main-title">Filter</h2>
                            <p className="filter-subtitle">Use the filters to get tailored results</p>
                        </div>
                        {isMobile && (
                            <div className="filter-mobile-arrow">
                                {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                        )}
                    </div>
                </div>

                <div className={`filter-accordion-content ${isMobile ? (isOpen ? 'open' : 'closed') : 'desktop'}`}>
                    <div className="filter-content-wrapper">
                        <FilterCheckboxGroup
                            title="Stops"
                            filters={stopsFilter}
                            setFilters={setStopsFilter}
                        />

                        <FilterCheckboxGroup
                            title="Operators"
                            filters={trainsFilter}
                            setFilters={setTrainsFilter}
                            scrollable={true}
                        />

                        <FilterCheckboxGroup
                            title="Sources"
                            filters={sourcesFilter}
                            setFilters={setSourcesFilter}
                            scrollable={true}
                        />

                        <FilterCheckboxGroup
                            title="Destinations"
                            filters={destinationsFilter}
                            setFilters={setDestinationsFilter}
                            scrollable={true}
                        />
                        {Object.keys(viaFilter).length > 0 && (
                            <FilterCheckboxGroup
                                title="Via"
                                filters={viaFilter}
                                setFilters={setViaFilter}
                                scrollable={true}
                            />
                        )}

                        <div className="filter-section-container">
                            <div className="filter-section-header">
                                <h3 className="filter-section-title">Availability</h3>
                            </div>
                            <div className="filter-checkbox-group">
                                <label className="filter-checkbox-label">
                                    <input
                                        type="checkbox"
                                        className="hidden"
                                        checked={showFullyAvailable}
                                        onChange={() => setShowFullyAvailable(!showFullyAvailable)}
                                    />
                                    <div className={`filter-checkbox-box ${showFullyAvailable ? 'checked' : ''}`}>
                                        {showFullyAvailable && <Check size={14} className="text-white" strokeWidth={3} />}
                                    </div>
                                    <span className="filter-checkbox-text">Show fully available routes</span>
                                </label>
                                {showHideCancelled && (
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={hideCancelled}
                                            onChange={() => setHideCancelled(!hideCancelled)}
                                        />
                                        <div className={`filter-checkbox-box ${hideCancelled ? 'checked' : ''}`}>
                                            {hideCancelled && <Check size={14} className="text-white" strokeWidth={3} />}
                                        </div>
                                        <span className="filter-checkbox-text">Hide Cancelled Trains</span>
                                    </label>
                                )}
                            </div>
                            <div className="filter-section-divider"></div>
                        </div>

                        {showAvoidStationChange && (
                            <div className="filter-section-container">
                                <div className="filter-section-header">
                                    <h3 className="filter-section-title">Transfer Preferences</h3>
                                </div>
                                <div className="filter-checkbox-group">
                                    <label className="filter-checkbox-label">
                                        <input
                                            type="checkbox"
                                            className="hidden"
                                            checked={avoidStationChange}
                                            onChange={() => setAvoidStationChange(!avoidStationChange)}
                                        />
                                        <div className={`filter-checkbox-box ${avoidStationChange ? 'checked' : ''}`}>
                                            {avoidStationChange && <Check size={14} className="text-white" strokeWidth={3} />}
                                        </div>
                                        <span className="filter-checkbox-text">Avoid Station/Airport Change</span>
                                    </label>
                                </div>
                                <div className="filter-section-divider"></div>
                            </div>
                        )}

                        <div className="filter-range-container">
                            <h3 className="filter-range-title">Departure Time</h3>
                            <RangeSlider
                                min={0}
                                max={maxDeparture}
                                value={departureTimeFilter}
                                onChange={setDepartureTimeFilter}
                                formatLabel={formatTime}
                            />
                            <div className="filter-section-divider" style={{ marginTop: '0' }}></div>
                        </div>

                        <div className="filter-range-container">
                            <h3 className="filter-range-title">Duration (Hrs)</h3>
                            <RangeSlider
                                min={0}
                                max={maxDuration}
                                value={durationFilter}
                                onChange={setDurationFilter}
                            />
                            <div className="filter-section-divider" style={{ marginTop: '0' }}></div>
                        </div>

                        <div className="filter-range-container">
                            <h3 className="filter-range-title">Fare (INR)</h3>
                            <RangeSlider
                                min={0}
                                max={maxFare}
                                value={fareFilter}
                                onChange={setFareFilter}
                                formatLabel={(v) => `₹${v.toLocaleString()}`}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </aside>
    );
};


export default FilterSidebar;
